import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Palette } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a graphic designer include on an invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A graphic designer invoice should include your name or studio name, the client name and project name, a unique invoice number and date, a description of the work (logo design, branding, web graphics, etc.), your rate (hourly or project fee), number of revisions included, any rush fees or licensing fees, subtotal, applicable tax, and the total amount due with your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do graphic designers charge clients — hourly or per project?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both models are common. Hourly billing works well for ongoing work, revisions, and retainers. Project-based (fixed fee) billing suits defined deliverables like a logo package or brand identity. Many designers charge a project fee with a set number of revisions included, then bill hourly for additional rounds. Whatever model you use, list it clearly on your invoice.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should graphic designers charge for revisions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — revisions beyond the agreed number should be charged. Define revision rounds in your contract and reflect them on your invoice. Add a separate line item for "Additional revisions" with your hourly or per-round rate. This sets clear expectations and prevents scope creep from eroding your project fee.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to add VAT or GST as a freelance graphic designer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It depends on your country and annual turnover. In the UK, register for VAT if your turnover exceeds £90,000 and add 20% VAT to invoices. In India, graphic designers earning over ₹20 lakh must register for GST and charge 18%. In Australia, add 10% GST if your turnover exceeds AUD $75,000. In the US, most design services are not subject to sales tax, though rules vary by state.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this graphic designer invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free graphic designer invoice template. Fill in your project details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function GraphicDesignerInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Graphic Designer Invoice Template: Free PDF for Designers | Invilic"
        description="Free graphic designer invoice template. Bill for logo design, branding, revisions, and licensing. Download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/graphic-designer-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Graphic Designer Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for graphic designers and creative studios. Bill for design projects, revisions, and licences. Fill in online and download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-pink-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Designer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Logo & brand identity designers', desc: 'Invoice for logo concepts, brand guidelines, typography, and colour palette deliverables.' },
              { title: 'UI & web designers', desc: 'Bill for wireframes, mockups, design systems, and handoff files by the hour or per project.' },
              { title: 'Print & packaging designers', desc: 'Invoice for brochures, packaging artwork, and print-ready files with clear revision terms.' },
              { title: 'Motion & social media designers', desc: 'Bill for animated graphics, social templates, and ongoing retainer-based creative work.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-pink-100 dark:bg-pink-900/40 flex items-center justify-center flex-shrink-0">
                  <Palette className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Graphic Designer Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Designer / studio name and logo',
              'Client name and project name',
              'Invoice number and date',
              'Payment due date',
              'Project description per line item',
              'Hours or project fee',
              'Number of revisions included',
              'Additional revision charges',
              'Licensing / usage rights fee',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-pink-100 dark:border-pink-800/30 bg-pink-50 dark:bg-pink-900/10">
                <CheckCircle className="w-4 h-4 text-pink-600 dark:text-pink-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Design Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your project details', desc: 'Enter your work, client info, and any revision or licensing charges.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to email to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-pink-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Graphic Designer Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Design Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-pink-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Graphic Designer Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
